/**
 * Reads the rendered line breakdown off a text asset's renderer metrics. The
 * metrics only exist once the asset has been measured/rendered, so callers
 * must treat `null` as "unknown", not "no lines".
 */

interface RenderedLineMetrics {
    text?: string;
    width?: number;
}

interface AssetWithMetrics {
    renderer?: {
        metrics?: {
            lines?: RenderedLineMetrics[];
        };
    };
}

export interface RenderedTextLine {
    text: string;
    width: number | null;
}

export function getRenderedTextLines(asset: unknown): RenderedTextLine[] | null {
    try {
        const lines = (asset as AssetWithMetrics)?.renderer?.metrics?.lines;
        if (!lines || lines.length === 0) {
            return null;
        }
        return lines
            .map((line) => ({
                text: (line.text ?? '').trim(),
                width: typeof line.width === 'number' ? line.width : null,
            }))
            .filter((line) => line.text.length > 0);
    } catch {
        // Some renderer getters throw when the asset has no measured text yet.
        return null;
    }
}

/**
 * A single-word line only reads as stranded when it's visually much shorter
 * than the line it broke away from; a lone word nearly as wide as its
 * neighbor is deliberate stacking, not a wrap accident.
 */
const WIDOW_MAX_NEIGHBOR_WIDTH_RATIO = 0.5;

/**
 * Indices (0-based) of rendered lines that hold a single stranded word — the
 * widow/orphan pattern: a lone word among multi-word lines, rendered at less
 * than half the width of its neighboring line. Not widows: a one-line asset
 * holding a single word (a short label), a stacked block where EVERY line is
 * a single word (deliberate styling like "Meet" / "Lumen5"), and a lone word
 * wide enough to visually balance its neighbor. When width metrics are
 * missing, the word-count rule alone decides.
 */
export function findWidowLineIndices(lines: RenderedTextLine[]): number[] {
    if (lines.length < 2) {
        return [];
    }
    const wordCounts = lines.map((line) => line.text.split(/\s+/).filter(Boolean).length);
    if (!wordCounts.some((count) => count >= 2)) {
        return [];
    }
    const indices: number[] = [];
    wordCounts.forEach((count, index) => {
        if (count !== 1) {
            return;
        }
        const line = lines[index];
        const neighbor = lines[index === 0 ? 1 : index - 1];
        if (
            line.width !== null &&
            neighbor.width !== null &&
            neighbor.width > 0 &&
            line.width >= neighbor.width * WIDOW_MAX_NEIGHBOR_WIDTH_RATIO
        ) {
            return;
        }
        indices.push(index);
    });
    return indices;
}
